import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../data/mock';
import { Shield, Clock, Heart, Award, Users, CheckCircle, Star } from 'lucide-react';
import CTASection from '../components/CTASection';

const AboutPage = () => {
  const { language, t } = useLanguage();

  const features = [
    { icon: Shield, ...t.about.features[0] },
    { icon: Clock, ...t.about.features[1] },
    { icon: Heart, ...t.about.features[2] },
    { icon: Award, ...t.about.features[3] },
  ];

  const stats = [
    { number: '10+', label: language === 'sv' ? 'Års erfarenhet' : 'Years experience' },
    { number: '500+', label: language === 'sv' ? 'Nöjda kunder' : 'Happy customers' },
    { number: '5.0', label: language === 'sv' ? 'Snittbetyg' : 'Average rating', icon: Star },
    { number: '100%', label: language === 'sv' ? 'Nöjdgaranti' : 'Satisfaction guarantee' },
  ];

  const values = language === 'sv' ? [
    'Professionell och pålitlig service',
    'Miljövänliga rengöringsprodukter',
    'Utbildad och erfaren personal',
    'Flexibla bokningsalternativ',
    'Transparent prissättning',
    'Försäkrade och ID-kontrollerade',
  ] : [
    'Professional and reliable service',
    'Eco-friendly cleaning products',
    'Trained and experienced staff',
    'Flexible booking options',
    'Transparent pricing',
    'Insured and ID-verified',
  ];

  return (
    <main>
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.about.title}
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            {t.about.subtitle}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <p className="text-4xl font-bold text-purple-600">{stat.number}</p>
                  {stat.icon && <stat.icon className="text-orange-400 fill-orange-400" size={24} />}
                </div>
                <p className="text-gray-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Users size={16} />
                {language === 'sv' ? 'Vår historia' : 'Our Story'}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {language === 'sv' 
                  ? 'Välkommen till Stockholm Elite Städ' 
                  : 'Welcome to Stockholm Elite Cleaning'
                }
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  {language === 'sv' 
                    ? 'Vi på Stockholm Elite Städ hjälper dig gärna i vardagen. När du anlitar oss kan du känna dig trygg med att vi tar hand om ditt hem eller arbetsplats på bästa sätt.'
                    : 'We at Stockholm Elite Cleaning are happy to help you in everyday life. When you hire us, you can feel safe knowing that we take care of your home or workplace in the best way.'
                  }
                </p>
                <p>
                  {language === 'sv'
                    ? 'För oss kommer trygghet, kvalitet och omtanke i första hand. Vi vill att det ska kännas bra och tryggt före, under och efter vi städar hos dig.'
                    : 'For us, safety, quality and care come first. We want you to feel good and safe before, during and after we clean at your place.'
                  }
                </p>
                <p>
                  {language === 'sv'
                    ? 'Vårt mål är att ge dig den hjälp och avlastning just du behöver!'
                    : 'Our goal is to give you the help and relief you need!'
                  }
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={images.cleaning1}
                alt="Professional cleaning"
                loading="lazy"
                width={600}
                height={400}
                className="rounded-2xl shadow-2xl w-full object-cover h-[400px]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Shield className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {language === 'sv' ? 'Försäkrade' : 'Insured'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {language === 'sv' ? '& ID-kontrollerade' : '& ID-verified'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.about.whyUs}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
                  <feature.icon className="text-white" size={28} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {language === 'sv' ? 'Våra värderingar' : 'Our Values'}
              </h2>
              <p className="text-purple-200 mb-8 leading-relaxed">
                {language === 'sv'
                  ? 'Vi är ett litet företag som värnar om våra kunder. Vi strävar alltid efter att ge dig bästa möjliga service.'
                  : 'We are a small company that cares about our customers. We always strive to give you the best possible service.'
                }
              </p>
              <div className="grid gap-4">
                {values.map((value, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-white" size={14} />
                    </div>
                    <span className="text-purple-100">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={images.modernHome}
                alt="Modern clean home"
                loading="lazy"
                width={600}
                height={400}
                className="rounded-2xl shadow-2xl w-full object-cover h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default AboutPage;
